import { useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/layout/AppShell';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { Tooltip } from '@/components/ui/Tooltip';
import { toast } from '@/components/ui/Toaster';
import {
  useCreatePlan, useDeletePlan, usePatchPlan, usePlans,
  type PlanBody, type PlanRecord,
} from '@/api/plans';
import { DROPLET_SIZE_LABEL, type DropletSize } from '@/lib/droplet';
import { SIZE_ORDER, tierTone } from '@/lib/tier';

const usd = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const FEATURE_LABEL: Record<string, string> = { mcp: 'MCP', embedding: 'Embedding', sso: 'SSO' };

export function PlansRoute() {
  const plans = usePlans();
  const del = useDeletePlan();
  const [editing, setEditing] = useState<PlanRecord | null>(null);
  const [creating, setCreating] = useState(false);

  async function remove(plan: PlanRecord) {
    if (!window.confirm(`Delete the ${plan.name} plan? This cannot be undone.`)) return;
    try {
      await del.mutateAsync(plan.slug);
      toast.success('Plan deleted', `${plan.name} removed.`);
    } catch (e: any) {
      toast.danger('Delete failed', e?.message ?? 'Unknown error');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Internal"
        title="Plans"
        description="Plan presets — the limits enforced at provisioning and the licence every managed workspace fetches. Edits apply on each workspace's next daily licence check; no deploy needed."
      />
      <PageBody>
        <Card>
          <CardHeader
            title={`${plans.data?.length ?? '…'} plans`}
            description="Customers reference a plan from Settings; a plan in use cannot be deleted."
            actions={
              <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setCreating(true)}>
                New plan
              </Button>
            }
          />
          {plans.isLoading ? (
            <div className="p-6 text-sm text-fg-muted flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading plans…
            </div>
          ) : plans.isError ? (
            <EmptyState title="Couldn't load plans" description="The backend is unreachable or errored — check /admin/v1/plans." />
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[860px]">
                <THead>
                  <tr>
                    <TH>Plan</TH>
                    <TH><Hint text="Monthly list price, USD. Feeds the Costs & revenue page. Empty = custom / contract pricing.">Price</Hint></TH>
                    <TH><Hint text="Provision-time cap, enforced by this panel: how many instances can be created. Existing instances are never deleted by a change here.">Instances</Hint></TH>
                    <TH><Hint text="Largest droplet size offered when provisioning an instance on this plan.">Max size</Hint></TH>
                    <TH><Hint text="Licence cap enforced inside the customer's NousViz (invites blocked at the limit, nobody deleted). Reaches each workspace on its next daily licence check.">Users</Hint></TH>
                    <TH><Hint text="Licence cap on installed plugins, enforced inside the product with the same grandfathering promise.">Plugins</Hint></TH>
                    <TH><Hint text="Paid feature switches (MCP, embedding, SSO) delivered via the licence.">Features</Hint></TH>
                    <TH><Hint text="Customers currently on this plan. A plan in use cannot be deleted.">Customers</Hint></TH>
                    <TH></TH>
                  </tr>
                </THead>
                <TBody>
                  {plans.data!.map(p => (
                    <TR key={p.slug}>
                      <TD>
                        <div className="flex items-center gap-2">
                          <Badge tone={tierTone(p.slug)}>{p.name}</Badge>
                          <span className="font-mono text-xs text-fg-subtle">{p.slug}</span>
                        </div>
                      </TD>
                      <TD>{p.price_usd !== null ? `${usd(p.price_usd)}/mo` : 'Custom'}</TD>
                      <TD>{p.provision.max_instances ?? 'Unlimited'}</TD>
                      <TD className="font-mono text-xs">{p.provision.max_size ?? 'Any'}</TD>
                      <TD>{p.entitlement.limits.max_users ?? 'Unlimited'}</TD>
                      <TD>{p.entitlement.limits.max_plugins ?? 'Unlimited'}</TD>
                      <TD className="text-xs">
                        {Object.entries(p.entitlement.features)
                          .filter(([, on]) => on)
                          .map(([k]) => FEATURE_LABEL[k] ?? k)
                          .join(' · ') || '—'}
                      </TD>
                      <TD>{p.tenant_count}</TD>
                      <TD>
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" iconLeft={<Pencil size={12} />} onClick={() => setEditing(p)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost" size="sm" iconLeft={<Trash2 size={12} />}
                            disabled={p.tenant_count > 0 || p.slug === 'business'}
                            title={
                              p.slug === 'business' ? 'Business is the default plan and cannot be deleted.'
                              : p.tenant_count > 0 ? 'In use — move its customers to another plan first.'
                              : undefined
                            }
                            onClick={() => remove(p)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </Card>
      </PageBody>

      <PlanFormModal
        open={creating}
        onClose={() => setCreating(false)}
        plan={null}
      />
      <PlanFormModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        plan={editing}
      />
    </>
  );
}

/* ── Create / edit form ─────────────────────────────────────────────── */

const EMPTY: PlanBody = {
  slug: '',
  name: '',
  price_usd: null,
  provision: { max_instances: 1, max_size: 's-2vcpu-4gb' },
  entitlement: {
    limits: { max_users: 5, max_plugins: 10 },
    plugins: '*',
    smtp: null,
    ssl: 'wildcard',
    features: { mcp: false, embedding: false, sso: false },
  },
};

function PlanFormModal({ open, onClose, plan }: {
  open: boolean;
  onClose: () => void;
  plan: PlanRecord | null;
}) {
  const create = useCreatePlan();
  const patch = usePatchPlan();
  const pending = create.isPending || patch.isPending;

  // Remount per plan via key so state initialises from the record.
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={plan ? `Edit ${plan.name}` : 'New plan'}
      description={plan
        ? 'Changes apply to every customer on this plan — provisioning limits immediately, licences on each workspace\'s next daily check.'
        : 'Slug is the permanent reference key — it cannot be changed later.'}
      size="md"
    >
      <PlanForm
        key={plan?.slug ?? 'new'}
        initial={plan ?? EMPTY}
        slugLocked={plan !== null}
        pending={pending}
        onSubmit={async body => {
          try {
            if (plan) {
              const { slug, ...rest } = body;
              await patch.mutateAsync({ slug: plan.slug, ...rest });
              toast.success('Plan saved', `${body.name} updated.`);
            } else {
              await create.mutateAsync(body);
              toast.success('Plan created', `${body.name} is ready to assign.`);
            }
            onClose();
          } catch (e: any) {
            toast.danger('Save failed', e?.message ?? 'Unknown error');
          }
        }}
      />
    </Modal>
  );
}

function PlanForm({ initial, slugLocked, pending, onSubmit }: {
  initial: PlanBody;
  slugLocked: boolean;
  pending: boolean;
  onSubmit: (body: PlanBody) => void;
}) {
  const [slug, setSlug] = useState(initial.slug);
  const [name, setName] = useState(initial.name);
  const [price, setPrice] = useState(initial.price_usd === null ? '' : String(initial.price_usd));
  const [maxInstances, setMaxInstances] = useState(
    initial.provision.max_instances === null ? '' : String(initial.provision.max_instances),
  );
  const [maxSize, setMaxSize] = useState(initial.provision.max_size ?? '');
  const [maxUsers, setMaxUsers] = useState(
    initial.entitlement.limits.max_users === null ? '' : String(initial.entitlement.limits.max_users),
  );
  const [maxPlugins, setMaxPlugins] = useState(
    initial.entitlement.limits.max_plugins === null ? '' : String(initial.entitlement.limits.max_plugins),
  );
  const [features, setFeatures] = useState<Record<string, boolean>>(initial.entitlement.features);

  const validSlug = /^[a-z0-9][a-z0-9-]{0,40}$/.test(slug);
  const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s));
  const numbersOk = [price, maxInstances, maxUsers, maxPlugins].every(
    s => s.trim() === '' || (Number.isFinite(Number(s)) && Number(s) >= 0),
  );
  const canSubmit = validSlug && name.trim().length > 0 && numbersOk && !pending;

  function submit() {
    onSubmit({
      slug: slug.trim(),
      name: name.trim(),
      price_usd: numOrNull(price),
      provision: {
        max_instances: numOrNull(maxInstances),
        max_size: maxSize === '' ? null : maxSize,
      },
      entitlement: {
        ...initial.entitlement,
        limits: { max_users: numOrNull(maxUsers), max_plugins: numOrNull(maxPlugins) },
        features,
      },
    });
  }

  const inputCls = 'w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm';

  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug" hint={slugLocked ? 'immutable' : 'lowercase letters, digits, dashes'}>
          <input value={slug} onChange={e => setSlug(e.target.value)} disabled={slugLocked} className={inputCls + (slugLocked ? ' opacity-60 font-mono text-xs' : ' font-mono text-xs')} />
        </Field>
        <Field label="Display name">
          <input value={name} onChange={e => setName(e.target.value)} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Price ($/month)" hint="empty = custom / contract pricing">
          <input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" className={inputCls} placeholder="Custom" />
        </Field>
        <Field label="Max instances" hint="empty = unlimited">
          <input value={maxInstances} onChange={e => setMaxInstances(e.target.value)} inputMode="numeric" className={inputCls} placeholder="Unlimited" />
        </Field>
      </div>
      <Field label="Max server size" hint="largest droplet provisioning allows; Any = no ceiling">
        <select value={maxSize} onChange={e => setMaxSize(e.target.value)} className={inputCls}>
          <option value="">Any</option>
          {SIZE_ORDER.filter(s => s !== 'shared' && s !== 'byo').map(s => (
            <option key={s} value={s}>{DROPLET_SIZE_LABEL[s as DropletSize] ?? s}</option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Max users" hint="licence limit; empty = unlimited">
          <input value={maxUsers} onChange={e => setMaxUsers(e.target.value)} inputMode="numeric" className={inputCls} placeholder="Unlimited" />
        </Field>
        <Field label="Max plugins" hint="licence limit; empty = unlimited">
          <input value={maxPlugins} onChange={e => setMaxPlugins(e.target.value)} inputMode="numeric" className={inputCls} placeholder="Unlimited" />
        </Field>
      </div>
      <Field label="Paid features">
        <div className="flex items-center gap-4 pt-1">
          {Object.keys({ ...{ mcp: false, embedding: false, sso: false }, ...features }).map(k => (
            <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(features[k])}
                onChange={e => setFeatures({ ...features, [k]: e.target.checked })}
              />
              {FEATURE_LABEL[k] ?? k}
            </label>
          ))}
        </div>
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="primary" size="sm" disabled={!canSubmit} onClick={submit}
          iconLeft={pending ? <Loader2 size={13} className="animate-spin" /> : undefined}>
          {pending ? 'Saving…' : slugLocked ? 'Save plan' : 'Create plan'}
        </Button>
      </div>
    </div>
  );
}

function Hint({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <Tooltip content={<span>{text}</span>} maxWidth={280}>
      <span className="cursor-help underline decoration-dotted decoration-fg-subtle underline-offset-2">
        {children}
      </span>
    </Tooltip>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
        {label}
        {hint && <span className="normal-case tracking-normal font-normal ml-1.5 text-fg-subtle/80">({hint})</span>}
      </label>
      {children}
    </div>
  );
}